import Controller from '@ember/controller';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { apiEndpoint } from '../utils/endpoints';
import { emailRegex } from 'scaleflow/utils/regex-match-rules';

export default class LoginController extends Controller {
  @tracked email: string = '';
  @tracked loading: boolean = false;
  @tracked tooManyRequests: boolean = false;

  @action
  setEmail(e: any) {
    this.email = e.target.value;
  }

  isValidUrl(url: string) {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  get emailIsValid() {
    return emailRegex.test(this.email);
  }

  async fetchRedirectUrl(email: string): Promise<string> {
    // We inject the WAF token as a request header (x-aws-waf-token) rather than
    // relying on AwsWafIntegration.fetch() to set it via cookie. The cookie-based
    // approach requires `credentials: 'include'`, which in turn requires the server
    // to respond with a specific Access-Control-Allow-Origin (not a wildcard). The
    // API Gateway backend has CORS configured with allowOrigins: '*', making
    // credentialed requests impossible.
    const token = await (window.AwsWafIntegration as any).getToken();
    const payload = {
      data: { attributes: { email } },
    };
    const response = await fetch(`${apiEndpoint()}/auth/idp-lookup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-aws-waf-token': token,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error(await response.text());
    const { data } = await response.json();
    const redirect_url = data.attributes.redirect_url as string;
    if (!this.isValidUrl(redirect_url))
      throw new Error(`Invalid redirect URL: ${redirect_url}`);
    return redirect_url;
  }

  @action
  async lookupIdp(e: FormDataEvent) {
    e.preventDefault();
    if (!this.emailIsValid) return;
    this.loading = true;
    this.tooManyRequests = false;
    try {
      const redirect_url = await this.fetchRedirectUrl(this.email);
      const url = new URL(redirect_url);
      const clientId = url.searchParams.get('client_id');
      const state = btoa(JSON.stringify({ clientId }));
      url.searchParams.set('state', state);
      window.location.href = url.toString();
    } catch (err: any) {
      this.tooManyRequests = true;
      console.error(err);
    } finally {
      this.loading = false;
    }
  }
}
